/*!
 * Featured-collection tabs: prefetch hidden panels and swap them in.
 * The first tab stays in the HTML. This file is omitted when the section
 * setting "Load hidden tabs after the page opens" is turned off.
 */
function whenIdle(fn) {
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(() => fn(), { timeout: 2000 });
  } else {
    window.setTimeout(fn, 200);
  }
}

function fetchText(url, reload) {
  return fetch(url, {
    credentials: "same-origin",
    headers: { Accept: "text/html" },
    cache: reload ? "reload" : "default",
  }).then((response) => {
    if (!response.ok) throw new Error("HTTP " + response.status);
    return response.text();
  });
}

function applyImageSizes(root, sizes) {
  if (!sizes) return;
  root.querySelectorAll("img").forEach((img) => {
    img.setAttribute("sizes", sizes);
  });
}

function trimCards(root, limit) {
  const cards = root.querySelectorAll("product-card");
  for (let i = cards.length - 1; i >= limit; i -= 1) cards[i].remove();
}

function paintHiddenState(panel, grid) {
  if (!panel.hasAttribute("hidden") || grid.animations) return;
  const selector = grid.getAttribute("data-animation-selector");
  if (!selector) return;
  /* Only pre-hide when the theme's load animation is actually running.
     Otherwise cards injected into a hidden tab would stay at opacity 0. */
  const sample = document.querySelector('.js-animate-sequence [style*="opacity"]');
  if (!sample) return;
  grid.querySelectorAll(selector).forEach((el) => {
    el.style.opacity = "0";
  });
}

function inject(panel, html) {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const source = doc.querySelector("[data-dez-lazy-cards]");
  if (!source) throw new Error("missing cards");

  const expected = panel.getAttribute("data-collection-id");
  const got = source.getAttribute("data-dez-collection");
  if (expected && got && expected !== got) throw new Error("collection mismatch");

  const limit = Number(panel.getAttribute("data-product-limit")) || 48;
  trimCards(source, limit);
  applyImageSizes(source, panel.getAttribute("data-image-sizes"));

  const grid = panel.querySelector("[data-items]");
  if (!grid) throw new Error("missing grid");

  const template = document.createElement("template");
  template.innerHTML = source.innerHTML;
  grid.replaceChildren(template.content);
  paintHiddenState(panel, grid);
  /* product-card upgrades on connect (quick shop, swatches, spinner).
     items-scroll already watches [data-items] for new children.
     The grid's inView observer was registered on page load and reads
     its children when the panel is shown, so the fade matches a server tab. */
  panel.dispatchEvent(new CustomEvent("dez:product-cards:mounted", { bubbles: true }));
}

function dropDeadAnimations(host) {
  /* animations.js stores Motion controls on each grid when it scrolls into view.
     If a lazy tab is opened before its cards arrive, the grid is empty and Motion
     returns controls without cancel(); Animations.resetAnimations() then throws inside
     TabbedContent.openTab() after every panel was hidden. Drop those dead entries. */
  host.querySelectorAll("tab-panel .js-animate-sequence").forEach((el) => {
    if (!Array.isArray(el.animations)) return;
    const live = el.animations.filter((a) => a && a.animation && typeof a.animation.cancel === "function");
    if (live.length === el.animations.length) return;
    if (live.length) el.animations = live;
    else delete el.animations;
  });
}

function setupHost(host) {
  const panels = Array.from(host.querySelectorAll('tab-panel[data-dez-lazy="1"]'));
  if (!panels.length || host.dataset.dezLazyBound === "1") return;
  host.dataset.dezLazyBound = "1";

  const loads = new Map();

  function loadPanel(panel, force) {
    if (panel.dataset.dezState === "ready") return Promise.resolve(panel);
    if (!force && loads.has(panel)) return loads.get(panel);

    const url = panel.getAttribute("data-fetch-url");
    if (!url) return Promise.reject(new Error("missing url"));

    panel.dataset.dezState = "loading";
    const promise = fetchText(url, false)
      .catch(() => fetchText(url, true))
      .then((html) => {
        inject(panel, html);
        panel.dataset.dezState = "ready";
        return panel;
      })
      .catch((error) => {
        panel.dataset.dezState = "failed";
        loads.delete(panel);
        throw error;
      });

    loads.set(panel, promise);
    return promise;
  }

  function releaseHeight(panel) {
    if (!panel.hasAttribute("hidden")) host.style.minHeight = "";
  }

  function fallbackNavigate(panel, button) {
    const url = (button && button.dataset.updateLink) || panel.getAttribute("data-collection-url");
    if (url) window.location.assign(url);
  }

  function onActivate(panel, button) {
    const pending = panel.dataset.dezState === "failed" ? loadPanel(panel, true) : loadPanel(panel);
    pending.then(() => releaseHeight(panel)).catch(() => {
      if (panel.hasAttribute("hidden")) return;
      fallbackNavigate(panel, button);
    });
  }

  if (typeof host.openTab === "function") {
    const original = host.openTab.bind(host);
    host.openTab = function (id) {
      if (host.currentTab && host.currentTab.id === id) return;
      const button = id ? host.querySelector("#" + CSS.escape(id)) : null;
      const panelId = button && button.getAttribute("aria-controls");
      const panel = panelId ? host.querySelector("#" + CSS.escape(panelId)) : null;
      const waiting = panel && panel.getAttribute("data-dez-lazy") === "1" && panel.dataset.dezState !== "ready";

      if (waiting) host.style.minHeight = host.offsetHeight + "px";
      else host.style.minHeight = "";

      dropDeadAnimations(host);
      let result;
      try {
        result = original(id);
      } catch (error) {
        /* never leave every panel hidden */
        if (panel) panel.removeAttribute("hidden");
      }
      if (waiting) onActivate(panel, button);
      return result;
    };
  }

  host.querySelectorAll("[data-dez-lazy-trigger]").forEach((button) => {
    const start = () => {
      const panelId = button.getAttribute("aria-controls");
      const panel = panelId ? host.querySelector("#" + CSS.escape(panelId)) : null;
      if (!panel) return;
      loadPanel(panel, false).catch(() => {});
    };
    button.addEventListener("pointerenter", start, { passive: true });
    button.addEventListener("touchstart", start, { passive: true });
    button.addEventListener("focus", start);
  });

  function prefetch() {
    panels.reduce((chain, panel) => {
      return chain.then(() => loadPanel(panel, false).catch(() => {}));
    }, Promise.resolve());
  }

  if (document.readyState === "complete") whenIdle(prefetch);
  else window.addEventListener("load", () => whenIdle(prefetch), { once: true });
}

function boot() {
  document.querySelectorAll("tabbed-content[data-dez-lazy-tabs]").forEach(setupHost);
}

if (window.customElements && window.customElements.get("tabbed-content")) boot();
else window.customElements.whenDefined("tabbed-content").then(boot);

document.addEventListener("shopify:section:load", (event) => {
  if (!event.target) return;
  event.target.querySelectorAll("tabbed-content[data-dez-lazy-tabs]").forEach(setupHost);
});
